import React, { useEffect, useState } from 'react';
import { Settings, Zap, PauseCircle, Clock, AlertTriangle, Save, Bell, MessageSquare } from 'lucide-react';
import { usePortalStore } from '../../store';
import type { SpeedMode, DiscordLevel } from '../../types';

const SPEED_MODES: { value: SpeedMode; label: string; desc: string; icon: React.ReactNode }[] = [
  {
    value: 'full',
    label: 'Full Speed',
    desc: 'Petri net runs continuously. Agents execute as tasks queue.',
    icon: <Zap className="w-5 h-5 text-emerald-400" />,
  },
  {
    value: 'scheduled',
    label: 'Scheduled',
    desc: 'Company runs on defined windows (e.g. weekdays 9–5, pause weekends).',
    icon: <Clock className="w-5 h-5 text-cyan-400" />,
  },
  {
    value: 'budget-capped',
    label: 'Budget-Capped',
    desc: 'Runs until monthly token budget is reached, then pauses automatically.',
    icon: <AlertTriangle className="w-5 h-5 text-yellow-400" />,
  },
  {
    value: 'paused',
    label: 'Paused',
    desc: 'All autonomous work halted. Resume any time.',
    icon: <PauseCircle className="w-5 h-5 text-red-400" />,
  },
];

const KNOWN_DEPARTMENTS = ['Engineering', 'Product', 'Marketing', 'Research', 'Finance'];

const DISCORD_LEVELS: { value: DiscordLevel; label: string; desc: string }[] = [
  { value: 'quiet',   label: 'Quiet',   desc: 'Only critical blockers — minimal noise' },
  { value: 'normal',  label: 'Normal',  desc: 'Blockers + completed epics + key signals' },
  { value: 'verbose', label: 'Verbose', desc: 'All activity including status changes' },
];

export const SettingsView: React.FC = () => {
  const {
    settings, burnRate, loadSettings, updateSettings, setDepartmentPause,
    alertThresholds, loadAlertThresholds, setAlertThresholds,
    discordLevel, loadDiscordLevel, setDiscordLevel,
  } = usePortalStore();

  const [budgetInput, setBudgetInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [savedMsg, setSavedMsg] = useState('');
  const [tokenWarnInput, setTokenWarnInput] = useState('');
  const [queueWarnInput, setQueueWarnInput] = useState('');
  const [savingThresholds, setSavingThresholds] = useState(false);

  useEffect(() => {
    loadSettings();
    loadAlertThresholds();
    loadDiscordLevel();
  }, []);

  useEffect(() => {
    if (alertThresholds) {
      setTokenWarnInput(String(alertThresholds.token_burn_rate_warn));
      setQueueWarnInput(String(alertThresholds.queue_depth_warn));
    }
  }, [alertThresholds]);

  useEffect(() => {
    if (settings) {
      setBudgetInput(String(settings.monthly_token_budget));
    }
  }, [settings]);

  const handleSpeedModeChange = async (mode: SpeedMode) => {
    await updateSettings({ speed_mode: mode });
  };

  const handleBudgetSave = async () => {
    const parsed = parseInt(budgetInput, 10);
    if (!Number.isFinite(parsed) || parsed <= 0) return;
    setSaving(true);
    await updateSettings({ monthly_token_budget: parsed });
    setSaving(false);
    setSavedMsg('Saved');
    setTimeout(() => setSavedMsg(''), 2000);
  };

  const handleDeptToggle = async (dept: string, currentlyPaused: boolean) => {
    await setDepartmentPause(dept, !currentlyPaused);
  };

  const handleNotifPref = async (key: keyof NonNullable<typeof settings>['notification_prefs'], val: boolean) => {
    if (!settings) return;
    await updateSettings({
      notification_prefs: { ...settings.notification_prefs, [key]: val },
    });
  };

  const handleThresholdSave = async () => {
    const tokenWarn = parseInt(tokenWarnInput, 10);
    const queueWarn = parseInt(queueWarnInput, 10);
    if (!Number.isFinite(tokenWarn) || tokenWarn <= 0) return;
    if (!Number.isFinite(queueWarn) || queueWarn <= 0) return;
    setSavingThresholds(true);
    await setAlertThresholds({ token_burn_rate_warn: tokenWarn, queue_depth_warn: queueWarn });
    setSavingThresholds(false);
  };

  if (!settings) {
    return (
      <div className="space-y-4 animate-pulse">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="bg-slate-800 border border-slate-700 rounded-lg h-24" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-8 max-w-2xl">
      <div>
        <div className="flex items-center gap-3 mb-1">
          <Settings className="w-6 h-6 text-slate-400" />
          <h1 className="text-3xl font-bold text-slate-100">Settings</h1>
        </div>
        <p className="text-slate-500 text-sm">Equip your co-founder. Control the speed, budget, and reach of your autonomous company.</p>
      </div>

      {/* Speed Mode */}
      <section className="bg-slate-800 border border-slate-700 rounded-lg p-6 space-y-4">
        <h2 className="text-sm font-semibold text-slate-400 uppercase tracking-wider">Company Speed</h2>
        <p className="text-xs text-slate-500">Token burn rate = company speed. You are the governor.</p>
        <div className="grid grid-cols-2 gap-3">
          {SPEED_MODES.map(({ value, label, desc, icon }) => (
            <button
              key={value}
              onClick={() => handleSpeedModeChange(value)}
              className={`flex items-start gap-3 p-4 rounded-lg border text-left transition-all ${
                settings.speed_mode === value
                  ? 'border-cyan-600 bg-cyan-950/30'
                  : 'border-slate-700 bg-slate-900/50 hover:border-slate-600'
              }`}
            >
              <div className="shrink-0 mt-0.5">{icon}</div>
              <div>
                <div className="font-semibold text-slate-100 text-sm mb-0.5">{label}</div>
                <div className="text-xs text-slate-400">{desc}</div>
              </div>
            </button>
          ))}
        </div>
      </section>

      {/* Monthly Token Budget */}
      <section className="bg-slate-800 border border-slate-700 rounded-lg p-6 space-y-4">
        <h2 className="text-sm font-semibold text-slate-400 uppercase tracking-wider">Monthly Token Budget</h2>
        <p className="text-xs text-slate-500">Hard cap on autonomous agent token consumption. Budget-Capped mode enforces this.</p>
        {burnRate && (
          <div className="text-xs text-slate-400">
            Current month: <span className="text-slate-200 font-semibold">{burnRate.monthly_used.toLocaleString()}</span> / {burnRate.monthly_budget.toLocaleString()} tokens used ({burnRate.monthly_pct.toFixed(0)}%)
          </div>
        )}
        <div className="flex items-center gap-3">
          <input
            type="number"
            value={budgetInput}
            onChange={e => setBudgetInput(e.target.value)}
            className="flex-1 bg-slate-900 border border-slate-600 rounded-lg px-4 py-2.5 text-slate-100 text-sm focus:outline-none focus:border-cyan-500"
            placeholder="e.g. 2000000"
            min={1}
          />
          <button
            onClick={handleBudgetSave}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2.5 bg-cyan-800 hover:bg-cyan-700 disabled:opacity-50 text-cyan-100 text-sm font-semibold rounded-lg transition-colors"
          >
            <Save className="w-4 h-4" />
            {saving ? 'Saving...' : savedMsg || 'Save'}
          </button>
        </div>
      </section>

      {/* Per-Department Throttle */}
      <section className="bg-slate-800 border border-slate-700 rounded-lg p-6 space-y-4">
        <h2 className="text-sm font-semibold text-slate-400 uppercase tracking-wider">Department Throttle</h2>
        <p className="text-xs text-slate-500">Pause individual departments without stopping the whole company. In-flight tasks complete; new tasks do not start.</p>
        <div className="space-y-2">
          {KNOWN_DEPARTMENTS.map(dept => {
            const paused = settings.department_pauses[dept] === true;
            return (
              <div key={dept} className="flex items-center justify-between py-2 border-b border-slate-700/50 last:border-0">
                <span className="text-sm text-slate-200">{dept}</span>
                <button
                  onClick={() => handleDeptToggle(dept, paused)}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                    paused
                      ? 'bg-red-900/60 text-red-300 hover:bg-red-900'
                      : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                  }`}
                >
                  {paused ? <><PauseCircle className="w-3.5 h-3.5" /> Paused</> : <><Zap className="w-3.5 h-3.5" /> Running</>}
                </button>
              </div>
            );
          })}
        </div>
      </section>

      {/* Notification Preferences */}
      <section className="bg-slate-800 border border-slate-700 rounded-lg p-6 space-y-4">
        <h2 className="text-sm font-semibold text-slate-400 uppercase tracking-wider">Notification Preferences</h2>
        <p className="text-xs text-slate-500">Choose what rises to a card in the notification queue vs. stays in the newsfeed.</p>
        <div className="space-y-3">
          {[
            { key: 'blockers_to_card' as const, label: '🔴 Blockers → Card', desc: 'Blocked epics always show as cards' },
            { key: 'challenges_to_card' as const, label: '🟡 Challenges → Card', desc: 'Intelligence that challenges a canvas assumption' },
            { key: 'signals_to_card' as const, label: '🟢 Signals → Card', desc: 'Relevant market signals (high-volume — use sparingly)' },
          ].map(({ key, label, desc }) => {
            const val = settings.notification_prefs[key];
            return (
              <div key={key} className="flex items-center justify-between gap-4">
                <div>
                  <div className="text-sm text-slate-200">{label}</div>
                  <div className="text-xs text-slate-500">{desc}</div>
                </div>
                <button
                  onClick={() => handleNotifPref(key, !val)}
                  className={`w-11 h-6 rounded-full relative transition-colors ${val ? 'bg-cyan-600' : 'bg-slate-600'}`}
                >
                  <span
                    className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-all ${val ? 'left-6' : 'left-1'}`}
                  />
                </button>
              </div>
            );
          })}
        </div>
      </section>

      {/* Alert Thresholds */}
      <section className="bg-slate-800 border border-slate-700 rounded-lg p-6 space-y-4">
        <h2 className="text-sm font-semibold text-slate-400 uppercase tracking-wider flex items-center gap-2">
          <Bell className="w-4 h-4" />
          Alert Thresholds
        </h2>
        <p className="text-xs text-slate-500">Define when the TopBar resources turn amber/red.</p>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-xs text-slate-400 mb-1">Token burn rate warn (tokens/wk)</label>
            <input
              type="number"
              value={tokenWarnInput}
              onChange={e => setTokenWarnInput(e.target.value)}
              min={1}
              className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-slate-100 text-sm focus:outline-none focus:border-cyan-500"
            />
          </div>
          <div>
            <label className="block text-xs text-slate-400 mb-1">Queue depth warn (items)</label>
            <input
              type="number"
              value={queueWarnInput}
              onChange={e => setQueueWarnInput(e.target.value)}
              min={1}
              className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-slate-100 text-sm focus:outline-none focus:border-cyan-500"
            />
          </div>
        </div>
        <button
          onClick={handleThresholdSave}
          disabled={savingThresholds}
          className="flex items-center gap-2 px-4 py-2 bg-cyan-800 hover:bg-cyan-700 disabled:opacity-50 text-cyan-100 text-sm font-semibold rounded-lg transition-colors"
        >
          <Save className="w-4 h-4" />
          {savingThresholds ? 'Saving...' : 'Save Thresholds'}
        </button>
      </section>

      {/* Discord Notification Level */}
      <section className="bg-slate-800 border border-slate-700 rounded-lg p-6 space-y-4">
        <h2 className="text-sm font-semibold text-slate-400 uppercase tracking-wider flex items-center gap-2">
          <MessageSquare className="w-4 h-4" />
          Discord Notification Level
        </h2>
        <p className="text-xs text-slate-500">Control how chatty Lucas is in your Discord server.</p>
        <div className="space-y-2">
          {DISCORD_LEVELS.map(({ value, label, desc }) => (
            <button
              key={value}
              onClick={() => setDiscordLevel(value)}
              className={`w-full flex items-start gap-3 p-3 rounded-lg border text-left transition-all ${
                discordLevel === value
                  ? 'border-cyan-600 bg-cyan-950/30'
                  : 'border-slate-700 bg-slate-900/50 hover:border-slate-600'
              }`}
            >
              <div className={`w-3 h-3 rounded-full mt-0.5 shrink-0 ${
                discordLevel === value ? 'bg-cyan-400' : 'bg-slate-600'
              }`} />
              <div>
                <div className="font-semibold text-slate-100 text-sm">{label}</div>
                <div className="text-xs text-slate-400">{desc}</div>
              </div>
            </button>
          ))}
        </div>
      </section>

      {/* Watchlist editing — Tier 2 change note */}
      <section className="bg-slate-800 border border-slate-700 rounded-lg p-6 space-y-3">
        <h2 className="text-sm font-semibold text-slate-400 uppercase tracking-wider">WATCHLIST.md</h2>
        <p className="text-xs text-slate-500">Define monitoring sources for the Watchtower (Tier 2 change — logged in DECISION-LOG.md).</p>
        <a
          href="#"
          onClick={e => { e.preventDefault(); }}
          className="inline-flex items-center gap-2 text-xs text-cyan-400 hover:text-cyan-300 transition-colors"
        >
          Edit WATCHLIST.md via Document Browser
        </a>
      </section>
    </div>
  );
};
